import React, { useEffect, useState } from "react";
import { CrewPilotModuleShell } from "../CrewPilotModuleShell";
import { CrewPilotToast } from "../CrewPilotToast";
import { CPDropdown } from "../CPDropdown";
import { usePermissions } from "../../hooks/usePermissions";
import {
  deleteEstimateWorkType,
  deleteMeasurement,
  formatSurfaceTotals,
  loadAssemblyTemplatesForFeatureType,
  updateEstimateWorkTypeAssembly,
  updateEstimateWorkTypeName,
  workTypeLabel,
} from "../../helpers/estimates";
import {
  findWorkTypeTemplateByName,
  replaceWorkTypeTemplate,
  saveWorkTypeTemplate,
} from "../../helpers/workTypeTemplates";
import {
  computeWorkTypeLabourTotal,
  computeWorkTypeMaterialsTotal,
  computeWorkTypeTotal,
  computeWorkTypeTotalSqft,
  formatCurrency,
} from "../../helpers/estimateTotals";
import { WorkspaceMeasurementSection } from "../workspace/WorkspaceChrome";
import { WorkspaceOverflowMenu } from "../workspace/WorkspaceOverflowMenu";
import { FeatureLabourSection } from "./FeatureLabourSection";
import { FeatureMaterialsSection } from "./FeatureMaterialsSection";
import { MeasurementFormSheet } from "./MeasurementFormSheet";
import { WorkTypeNameDialog } from "./WorkTypeNameDialog";
import { WorkTypeTemplateConflictDialog } from "./WorkTypeTemplateConflictDialog";
import { useIsDesktopViewport } from "../../hooks/useIsDesktopViewport";
import { FeatureWorkspaceDesktop } from "./desktop/FeatureWorkspaceDesktop";
import "./estimateWorkspace.css";

/**
 * SCREEN 3 — Feature Workspace.
 *
 * Measurements/Materials/Labour now read as visual siblings: a compact
 * uppercase section label, compact rows (tap to edit, "⋯" for delete), and
 * a consistent "+ Add ..." action — Measurements via MeasurementFormSheet,
 * Materials/Labour via the new FeatureMaterialsSection/FeatureLabourSection
 * (which reuse the exact same CRUD helpers the old inline panels used).
 *
 * Assembly is hidden entirely when no assemblies are available for this
 * Feature type, instead of showing a large disabled dropdown.
 */
export function FeatureWorkspaceScreen({
  workType,
  materialsCatalog,
  editable,
  organizationId = null,
  onReload,
  onTemplatesChanged,
  onBack,
  onFeatureDeleted,
  parentLabel,
}) {
  const isDesktop = useIsDesktopViewport();
  const { actor } = usePermissions();
  const [assemblies, setAssemblies] = useState([]);
  const [assembliesLoaded, setAssembliesLoaded] = useState(false);
  const [showAddMeasurement, setShowAddMeasurement] = useState(false);
  const [editingSection, setEditingSection] = useState(null);
  const [showRenameDialog, setShowRenameDialog] = useState(false);
  const [showSaveTemplateDialog, setShowSaveTemplateDialog] = useState(false);
  const [saveTemplateInitialName, setSaveTemplateInitialName] = useState("");
  const [templateConflict, setTemplateConflict] = useState(null);
  const [toastMessage, setToastMessage] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState("");

  const label = workTypeLabel(workType);
  const sections = workType.sections || [];
  const surfaceTotals = formatSurfaceTotals(workType.surface_totals);
  const netSqft = computeWorkTypeTotalSqft(workType);
  const featureTotal = computeWorkTypeTotal(workType, materialsCatalog);

  useEffect(() => {
    let cancelled = false;
    async function loadAssemblies() {
      if (!workType.feature_type_id) {
        setAssemblies([]);
        setAssembliesLoaded(true);
        return;
      }
      const rows = await loadAssemblyTemplatesForFeatureType(workType.feature_type_id);
      if (!cancelled) {
        setAssemblies(rows || []);
        setAssembliesLoaded(true);
      }
    }
    loadAssemblies();
    return () => {
      cancelled = true;
    };
  }, [workType.feature_type_id]);

  async function handleAssemblyChange(assemblyTemplateId) {
    if (!editable) return;
    try {
      await updateEstimateWorkTypeAssembly(
        actor,
        workType.estimate_feature_id,
        assemblyTemplateId || null,
      );
      await onReload?.();
    } catch (err) {
      console.error("[FeatureWorkspaceScreen] Assembly update failed:", err);
      setError(err.message || "Unable to save assembly.");
    }
  }

  async function handleDeleteFeature() {
    if (!editable) return;
    if (!window.confirm(`Delete feature "${label}"?`)) return;
    setDeleting(true);
    setError("");
    try {
      await deleteEstimateWorkType(actor, workType.estimate_feature_id);
      await onFeatureDeleted?.();
    } catch (err) {
      console.error("[FeatureWorkspaceScreen] Delete failed:", err);
      setError(err.message || "Unable to delete feature.");
    } finally {
      setDeleting(false);
    }
  }

  async function handleDeleteMeasurement(section) {
    if (!editable) return;
    if (!window.confirm(`Delete measurement "${section.name}"?`)) return;
    try {
      await deleteMeasurement(actor, section.section_id);
      await onReload?.();
    } catch (err) {
      console.error("[FeatureWorkspaceScreen] Delete measurement failed:", err);
      setError(err.message || "Unable to delete measurement.");
    }
  }

  async function handleRenameFeature(name) {
    await updateEstimateWorkTypeName(actor, workType.estimate_feature_id, name);
    await onReload?.();
  }

  async function completeTemplateSave(templateName, existingTemplateId = null) {
    if (existingTemplateId) {
      await replaceWorkTypeTemplate(
        existingTemplateId,
        templateName,
        workType,
        organizationId,
      );
    } else {
      await saveWorkTypeTemplate(templateName, workType, organizationId);
    }
    onTemplatesChanged?.();
    setToastMessage(`Template "${templateName}" saved successfully.`);
  }

  async function handleSaveTemplate(templateName) {
    const existing = await findWorkTypeTemplateByName(templateName, organizationId);
    if (existing) {
      setShowSaveTemplateDialog(false);
      setTemplateConflict({ templateName, existingTemplate: existing });
      return false;
    }
    await completeTemplateSave(templateName);
  }

  function openSaveTemplateDialog(initialName = label) {
    setSaveTemplateInitialName(initialName);
    setShowSaveTemplateDialog(true);
  }

  const menuItems = editable
    ? [
        { label: "Rename", onClick: () => setShowRenameDialog(true) },
        { label: "Save as Template", onClick: () => openSaveTemplateDialog() },
        {
          label: deleting ? "Deleting…" : "Delete Feature",
          onClick: handleDeleteFeature,
          destructive: true,
          disabled: deleting,
        },
      ]
    : [];

  const overlays = (
    <>
      {showAddMeasurement && (
        <MeasurementFormSheet
          estimateFeatureId={workType.estimate_feature_id}
          existingSections={sections}
          onClose={() => setShowAddMeasurement(false)}
          onSaved={onReload}
        />
      )}

      {editingSection && (
        <MeasurementFormSheet
          estimateFeatureId={workType.estimate_feature_id}
          section={editingSection}
          existingSections={sections}
          onClose={() => setEditingSection(null)}
          onSaved={onReload}
        />
      )}

      {showRenameDialog && (
        <WorkTypeNameDialog
          title="Rename Feature"
          fieldLabel="Feature Name"
          initialName={label}
          onSave={handleRenameFeature}
          onClose={() => setShowRenameDialog(false)}
        />
      )}

      {showSaveTemplateDialog && (
        <WorkTypeNameDialog
          title="Save Template"
          fieldLabel="Template Name"
          emptyMessage="Template name is required."
          saveLabel="Save Template"
          initialName={saveTemplateInitialName || label}
          onSave={handleSaveTemplate}
          onClose={() => setShowSaveTemplateDialog(false)}
        />
      )}

      {templateConflict && (
        <WorkTypeTemplateConflictDialog
          templateName={templateConflict.templateName}
          onReplace={() =>
            completeTemplateSave(
              templateConflict.templateName,
              templateConflict.existingTemplate.work_type_template_id,
            )
          }
          onSaveAsNew={() => {
            setTemplateConflict(null);
            openSaveTemplateDialog(templateConflict.templateName);
          }}
          onClose={() => setTemplateConflict(null)}
        />
      )}

      {toastMessage && (
        <CrewPilotToast message={toastMessage} onDismiss={() => setToastMessage("")} />
      )}
    </>
  );

  if (isDesktop) {
    const measurementSummary =
      surfaceTotals.length > 0
        ? surfaceTotals.map((row) => `${row.label} ${row.sqft.toFixed(1)} ft²`).join(" · ")
        : "";
    return (
      <FeatureWorkspaceDesktop
        title={label}
        parentLabel={parentLabel}
        error={error}
        netSqft={netSqft}
        featureTotal={featureTotal}
        materialsTotal={computeWorkTypeMaterialsTotal(workType, materialsCatalog)}
        labourTotal={computeWorkTypeLabourTotal(workType)}
        surfaceTotals={surfaceTotals}
        menuItems={menuItems}
        onBack={onBack}
        assemblyNode={
          assembliesLoaded && (assemblies || []).length > 0 ? (
            <CPDropdown
              label="Assembly (optional)"
              value={workType.assembly_template_id || ""}
              onChange={handleAssemblyChange}
              options={assemblies.map((assembly) => ({
                value: assembly.assembly_template_id,
                label: assembly.name,
              }))}
              placeholder="Select assembly"
              disabled={!editable}
            />
          ) : null
        }
        measurementsNode={
          <WorkspaceMeasurementSection
            sections={sections}
            summary={measurementSummary}
            editable={editable}
            onAdd={() => setShowAddMeasurement(true)}
            onEdit={setEditingSection}
            onDelete={handleDeleteMeasurement}
          />
        }
        materialsNode={
          <FeatureMaterialsSection
            workType={workType}
            materialsCatalog={materialsCatalog}
            editable={editable}
            onReload={onReload}
          />
        }
        labourNode={
          <FeatureLabourSection
            workType={workType}
            materialsCatalog={materialsCatalog}
            editable={editable}
            onReload={onReload}
          />
        }
      >
        {overlays}
      </FeatureWorkspaceDesktop>
    );
  }

  return (
    <CrewPilotModuleShell title={label} sectionLabel="Feature" onBack={onBack}>
      {error && <div className="est-inline-error">{error}</div>}

      <div className="est-workspace-header-row">
        <div className="est-workspace-header-text est-page-subtitle-row">
          {netSqft > 0 && (
            <>
              <span>{netSqft.toFixed(0)} ft²</span>
              <span className="est-page-subtitle-dot">·</span>
            </>
          )}
          <span>{formatCurrency(featureTotal)}</span>
        </div>
        <WorkspaceOverflowMenu items={menuItems} ariaLabel="Feature actions" />
      </div>

      {assembliesLoaded && (assemblies || []).length > 0 && (
        <div className="est-assembly-row">
          <CPDropdown
            label="Assembly (optional)"
            value={workType.assembly_template_id || ""}
            onChange={handleAssemblyChange}
            options={assemblies.map((assembly) => ({
              value: assembly.assembly_template_id,
              label: assembly.name,
            }))}
            placeholder="Select assembly"
            disabled={!editable}
          />
        </div>
      )}

      <WorkspaceMeasurementSection
        sections={sections}
        summary={
          surfaceTotals.length > 0
            ? surfaceTotals.map((row) => `${row.label} ${row.sqft.toFixed(1)} ft²`).join(" · ")
            : ""
        }
        editable={editable}
        onAdd={() => setShowAddMeasurement(true)}
        onEdit={setEditingSection}
        onDelete={handleDeleteMeasurement}
      />

      <FeatureMaterialsSection
        workType={workType}
        materialsCatalog={materialsCatalog}
        editable={editable}
        onReload={onReload}
      />

      <FeatureLabourSection
        workType={workType}
        materialsCatalog={materialsCatalog}
        editable={editable}
        onReload={onReload}
      />

      <div className="est-total-row est-total-row--emphasis">
        <span className="est-total-row-label">Feature Total</span>
        <span className="est-total-row-value">{formatCurrency(featureTotal)}</span>
      </div>

      <div className="est-bottom-spacer" />

      {overlays}
    </CrewPilotModuleShell>
  );
}
